import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import loadable from '@loadable/component';

import { OnePageFragment, OwnerType } from '../../../types/__generated__';
import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import { Activity } from '../../components/Activity/Activity';
import { Banner } from '../../components/Banner/Banner';
import { CampaignStartFundraising } from '../../components/CampaignStartFundraising/CampaignStartFundraising';
import CharityRegNumber from '../../components/CharityRegNumber/CharityRegNumber';
import { CheckoutModal } from '../../components/CheckoutModal/CheckoutModal';
import ClosingDate from '../../components/ClosingDate/ClosingDate';
import { CopyProvider } from '../../components/CopyProvider/CopyProvider.onepage';
import { DonateToYourselfModal } from '../../components/DonateToYourselfModal/DonateToYourselfModal';
import { DonationSummary } from '../../components/DonationSummary/DonationSummary.onepage';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import { FitbitManager } from '../../components/FitnessManager/FitbitManager';
import { StravaManager } from '../../components/FitnessManager/StravaManager';
import FundraisersList from '../../components/FundraisersList/FundraisersList';
import FundraisingCTA from '../../components/FundraisingCTA/FundraisingCTA';
import { Helmet } from '../../components/Helmet/Helmet.onepage';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import { PageOwnerAvatar } from '../../components/PageOwnerAvatar/PageOwnerAvatar.onepage';
import PageOwnerTitle from '../../components/PageOwnerTitle/PageOwnerTitle';
import PageSection from '../../components/PageSection/PageSection';
import { PopupBlockedModal } from '../../components/PopupBlockedModal/PopupBlockedModal';
import { PopupRefocusModal } from '../../components/PopupRefocusModal/PopupRefocusModal';
import { PreviewLabel } from '../../components/PreviewLabel/PreviewLabel';
import Reason from '../../components/Reason/Reason';
import { ShareModal } from '../../components/ShareModal/ShareModal.onepage';
import { CampaignCreationSuccessShareProvider } from '../../components/SocialButtons/CampaignCreationSuccessShareProvider';
import { OnePageShareProvider } from '../../components/SocialButtons/OnePageShareProvider';
import SocialButtonsList from '../../components/SocialButtons/SocialButtonsList';
import Story from '../../components/Story/Story';
import SuccessMessage from '../../components/SuccessMessage/SuccessMessage';
import { SupportersList } from '../../components/SupportersList/SupportersList.onepage';
import TeamDetails from '../../components/TeamDetails/TeamDetails';
import { TeamsLeaderboardList } from '../../components/TeamsList/TeamsLeaderboard';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { Totals } from '../../components/Totals/Totals';
import WeDidItHeader from '../../components/WeDidItHeader/WeDidItHeader';
import { MODAL } from '../../context';
import {
  buildQuery,
  useApp,
  useBasePageQuery,
  useFlags,
  useOptimizely,
  usePageQuery,
} from '../../hooks';
import { useOpenDonateToYourselfModal } from '../../hooks/fundraising/useOpenDonateToYourselfModal';
import { useProduct } from '../../hooks/page/product/useProduct';
import { useInView } from '../../hooks/useinView';
import { useScript } from '../../hooks/useScript';
import { getFirstTeam } from '../../utils/relationships';
import Fragment from './OnePageFragment.gql';

import styles from './OnePage.scss';
import { PageTopBar } from '../../components/PageTopBar/PageTopBar';
import { NativeShareModal } from '../../components/NativeShareModal/NativeShareModal';
import { ShareModalPlatforms } from '../../components/NativeShareModal/NativeShareEnums';
import { ESSupportersList } from '../../components/ESSupportersList/ESSupportersList.onepage';
import { Experiments } from '../../constants/experiments';
import VerifiedBanner from '../../components/VerifiedBanner/VerifiedBanner';

import DonationPrompts from '../../components/DonationPrompts/DonationPrompts';
import isDesktopViewport from '../../utils/isDesktopViewport';
import { DonationReminderModal } from '../../components/DonationReminderModal/DonationReminderModal';
import GiftAidBanner from '../../components/GiftAidBanner/GiftAidBanner';
import TrendingBanner from '../../components/TrendingBanner/TrendingBanner';

const QRModal = loadable(() => import('../../components/QRModal/QRModal'));
const Editbar = loadable(() =>
  import('../../components/Editbar/Editbar.onepage'),
);
const TimelinePageSection = loadable(() =>
  import('../../components/Timeline/TimelinePageSection/TimelinePageSection'),
);
const TeamAndCompany = loadable(() =>
  import('../../components/TeamAndCompany/TeamAndCompany'),
);
const QualarooLoader = loadable(
  () => import('../../components/QualarooLoader/QualarooLoader'),
  { resolveComponent: module => module.QualarooLoader },
);
const StickySummary = loadable(() =>
  import('../../components/StickySummary/StickySummary'),
);

const OnePageQuery = buildQuery(Fragment);

export const OnePage: React.FunctionComponent = () => {
  useScript('//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js', {
    async: true,
  });
  const { inViewRef, inView, rootIsBelowTarget } = useInView();
  const { data } = usePageQuery<OnePageFragment>(OnePageQuery);
  const { data: basePageData } = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });
  const optimizely = useOptimizely();
  const { enableFitbit, shouldUseSupportersService } = useFlags();
  const { openModal, hideModal } = useApp();

  const isDesktop = typeof window !== 'undefined' ? isDesktopViewport() : false;

  const {
    data: { features, ownerType, name },
  } = useProduct();

  const isFRP = name === 'fundraising';

  useOpenDonateToYourselfModal({
    totalDonationAmount:
      basePageData?.page?.donationSummary?.totalAmount?.value,
  });

  const decision = optimizely?.decide(
    Experiments.ConsumerDarkslide.VerifiedCharity.flagKey,
  );

  let shouldUseGiftAidBanner;
  if (isFRP) {
    shouldUseGiftAidBanner = optimizely?.decide(
      Experiments.ConsumerDarkslide.GiftAidBanner.flagKey,
    );
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '',
    );
    const isNewPage = Boolean(urlParams.get('newPage'));

    if (isNewPage) {
      const handleBackClick = () => {
        window.history.forward();
      };

      window.history.pushState(null, 'null', window.location.href);
      window.addEventListener('popstate', handleBackClick);
      return () => window.removeEventListener('popstate', handleBackClick);
    }
  }, []);

  const displayTeamAndCompany = getFirstTeam(
    data?.relationships?.parents ?? [],
  );

  const displayCauseMini = features?.layout
    ? features.layout.CauseMiniEnabled
    : true;

  return (
    <OnePageShareProvider>
      <CopyProvider>
        <Page>
          {openModal === MODAL.DONATE_TO_YOURSELF && <DonateToYourselfModal />}
          {openModal === MODAL.SHARE && <ShareModal />}
          {openModal === MODAL.CHECKOUT && (
            <CheckoutModal onClose={hideModal} />
          )}
          {openModal === MODAL.POPUP_BLOCKED && (
            <PopupBlockedModal onClose={hideModal} />
          )}
          {openModal === MODAL.POPUP_REFOCUS && (
            <PopupRefocusModal onClose={hideModal} />
          )}
          {openModal === MODAL.INSTAGRAM_SHARE && (
            <NativeShareModal
              onClose={hideModal}
              buttonOptions={[
                { label: 'Instagram Post', value: 'Instagram Post' },
                { label: 'Instagram Story', value: 'Instagram Story' },
              ]}
              platform={ShareModalPlatforms.INSTAGRAM}
            />
          )}
          {openModal === MODAL.DONATION_REMINDER && (
            <DonationReminderModal onClose={hideModal} /> // OTG 2023 - Tom Hedges
          )}
          <IsQRCode>
            <QRModal />
          </IsQRCode>
          <PageAlert />
          <Banner />
          <PageTopBar>
            <Editbar />
            <PreviewLabel />
          </PageTopBar>
          <Helmet />
          {features?.ownerMessaging?.SuccessEnabled && (
            <CampaignCreationSuccessShareProvider>
              <SuccessMessage />
            </CampaignCreationSuccessShareProvider>
          )}
          {features?.consumerMessaging?.WeDidItEnabled && <WeDidItHeader />}
          <DefaultLayout
            slots={{
              featureImage: <FeatureImage enableLightbox />,
              ownerAvatar: <PageOwnerAvatar />,
              titles: <PageOwnerTitle />,
              totals: (
                <>
                  <TotaliserWrapper />
                  <Totals />
                </>
              ),
              callsToAction: (
                <div ref={inViewRef} className={styles.ctaWrapper}>
                  {isFRP && <TrendingBanner />}
                  <FundraisingCTA />
                  {shouldUseGiftAidBanner?.enabled && <GiftAidBanner />}
                </div>
              ),
              titlesSupplementary: (
                <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
                  <Activity />
                  <Reason />
                  {features?.teams?.teamDetailsEnabled && <TeamDetails />}
                  {ownerType === OwnerType.Organisation && <ClosingDate />}
                  <EventDetails />
                  {ownerType === OwnerType.Organisation && <CharityRegNumber />}
                </div>
              ),
              causeMini: (
                <>
                  {displayCauseMini && <AboutCauseMini />}
                  {data?.allowFundraising && <CampaignStartFundraising />}
                </>
              ),
              main: (
                <>
                  <PageSection
                    title={t('story.title')}
                    className="jg-pages-fundraising__story"
                    hasBorder={true}
                  >
                    <Story />
                    <h2 className="jg-h3 jg-text--center">
                      {t('story.shareTitle', 'Share this story')}
                    </h2>

                    <SocialButtonsList />
                  </PageSection>
                  {!isDesktop && isFRP && <DonationPrompts />}
                  {features?.timeline && <TimelinePageSection />}
                </>
              ),
              aside: (
                <>
                  {features?.thirdPartyApplications?.stravaEnabled && (
                    <PageSection>
                      <StravaManager />
                    </PageSection>
                  )}
                  {features?.thirdPartyApplications?.fitbitEnabled &&
                    enableFitbit && (
                      <PageSection>
                        <FitbitManager />
                      </PageSection>
                    )}
                  {isDesktop && isFRP && <DonationPrompts />}
                  <FundraisersList />
                  <TeamsLeaderboardList />
                  {shouldUseSupportersService && isFRP ? (
                    <ESSupportersList />
                  ) : (
                    <SupportersList />
                  )}
                </>
              ),
              teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
              contentFooterPrimary: (
                <AboutCampaignAndCharitySection withExternalUrl={true} />
              ),
              contentFooterSecondary: <DonationSummary />,
            }}
          />
          {decision?.enabled && <VerifiedBanner />}
          {!inView && rootIsBelowTarget && <StickySummary />}
          {ownerType === OwnerType.Individual && <QualarooLoader />}
        </Page>
      </CopyProvider>
    </OnePageShareProvider>
  );
};

OnePage.displayName = 'OnePage';

export default OnePage;
